import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';


const TicketFilters = ({ filters, onFilterChange, onReset }) => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    fetchTechnicians();
    fetchLocations();
  }, []);

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('personnel')
        ?.select('id, name')
        ?.eq('role', 'technician')
        ?.order('name', { ascending: true });

      if (error) {
        console.error('Error fetching technicians:', error?.message);
        return;
      }

      const technicianOptions = [
        { value: 'all', label: 'All Technicians' },
        ...(data?.map(tech => ({
          value: tech?.name,
          label: tech?.name
        })) || [])
      ];

      setTechnicians(technicianOptions);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        ?.from('customer_locations')
        ?.select('id, well_name, company')
        ?.order('well_name');

      if (error) throw error;

      setLocationOptions([
        { value: 'all', label: 'All Locations' },
        ...(data || [])?.map(loc => ({
          value: loc?.id,
          label: loc?.well_name,
          description: loc?.company || ''
        }))
      ]);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1 mb-4 md:mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Filter Tickets
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Reset
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Input
          type="search"
          placeholder="Search by ticket ID or customer..."
          value={filters?.search}
          onChange={(e) => onFilterChange('search', e?.target?.value)}
          className="w-full"
        />

        <Select
          placeholder="Select status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
        />

        <Select
          placeholder="Select priority"
          options={priorityOptions}
          value={filters?.priority}
          onChange={(value) => onFilterChange('priority', value)}
        />

        <Select
          placeholder="Select technician"
          options={technicians}
          value={filters?.technician}
          onChange={(value) => onFilterChange('technician', value)}
          searchable
          disabled={loading}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Input
          type="date"
          label="From Date"
          value={filters?.dateFrom}
          onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
        />

        <Input
          type="date"
          label="To Date"
          value={filters?.dateTo}
          onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
        />

        <Select
          placeholder="Filter by location"
          options={locationOptions}
          value={filters?.location || 'all'}
          onChange={(value) => onFilterChange('location', value)}
          searchable
        />
      </div>
    </div>
  );
};

export default TicketFilters;